
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchResult } from '@/services/search';

interface UseGlobalSearchUIProps {
  query: string;
  results: SearchResult[];
  searchHistory: string[];
  clearResults: () => void;
}

export const useGlobalSearchUI = ({
  query,
  results,
  searchHistory,
  clearResults
}: UseGlobalSearchUIProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();

  const showDropdown = isOpen && (query.trim() || searchHistory.length > 0);
  const displayResults = query.trim() ? results.slice(0, 5) : [];
  const displayHistory = !query.trim() ? searchHistory.slice(0, 5) : [];

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    setIsOpen(false);
    clearResults();
  };

  const handleHistoryClick = (historyQuery: string) => {
    // This will be handled by the parent component
    setIsOpen(true);
  };

  const handleShowAllResults = () => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    const totalItems = displayResults.length + displayHistory.length + (query.trim() && results.length > 5 ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < displayResults.length) {
            handleResultClick(displayResults[selectedIndex]);
          } else if (selectedIndex < displayResults.length + displayHistory.length) {
            const historyIndex = selectedIndex - displayResults.length;
            return displayHistory[historyIndex]; // Return the history query to be handled by parent
          } else {
            handleShowAllResults();
          }
        } else if (query.trim()) {
          handleShowAllResults();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        return 'escape';
    }
  };

  return {
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex,
    showDropdown,
    handleResultClick,
    handleHistoryClick,
    handleShowAllResults,
    handleKeyDown
  };
};
